import React, { useCallback, useEffect, useState, useRef } from 'react';
import { useForm, FormProvider } from 'react-hook-form';
import ReactHookFormInput from '../../custom-components/form-components/ReactHookFormInput';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogTitle from '@material-ui/core/DialogTitle';
import Paper from '@material-ui/core/Paper';
import { Button, debounce, CircularProgress } from '@material-ui/core';
import { Alert, AlertTitle } from '@material-ui/lab';
import { showMessage, addPayReportPaymentStatus } from 'app/store/actions';
import { getNextAvailableCheckNumber } from '../../repository';
import { useDispatch } from 'react-redux';
import { yupResolver } from '@hookform/resolvers/yup';
import { object, number } from 'yup';
import { PayReportDownloadStatus, PaymentFrequency, PaymentType, PayReportPageType } from '../../constants/report';

function PaperComponent(props) {
    return <Paper {...props} />;
}

const getNumberOfChecks = (payReports) => {
    if (!payReports || payReports.length === 0) return 0;
    return payReports.filter((report) => {
        return report.payment_method === 'check';
    }).length;
};

const formatCollisionList = (numbers) => {
    if (!numbers || numbers.length === 0) return '';
    const sorted = [...numbers].sort((a, b) => a - b);
    const ranges = [];
    let start = sorted[0];
    let end = sorted[0];
    for (let i = 1; i < sorted.length; i++) {
        if (sorted[i] === end + 1) {
            end = sorted[i];
        } else {
            ranges.push(start === end ? `${start}` : `${start}-${end}`);
            start = sorted[i];
            end = sorted[i];
        }
    }
    ranges.push(start === end ? `${start}` : `${start}-${end}`);
    return ranges.join(', ');
};

// This component is used in
//   PayReportDownloadManager, WeeklyAdvancePayReportsManager, and Admin Hauler/Vendor reports
//   All of the above either catches deferrals before it gets to this component, or otherwise cannot use the deferrals type
//   Therefore, we assume all tasks here are async
const ConfirmCheckNumberDialog = ({ dialogOpen, setDialogOpen, paymentType, payeeType, payReports, dispatchTask, startDate, endDate }) => {
    const dispatch = useDispatch();
    const numberOfChecks = getNumberOfChecks(payReports);

    const [isSubmitting, setIsSubmitting] = useState(false);
    const [preview, setPreview] = useState(null);
    const [previewLoading, setPreviewLoading] = useState(false);
    const [previewError, setPreviewError] = useState(null);
    const [forceCheckNumberInput, setForceCheckNumberInput] = useState(false);
    const previewAbortRef = useRef(null);
    const isAutoSettingRef = useRef(false);

    const getPaymentFrequency = () => {
        switch (paymentType) {
            case PaymentType.advance:
                return PaymentFrequency.semiMonthly;
            case PaymentType.settlement:
                return PaymentFrequency.monthly;
            case PaymentType.weeklyAdvance:
                return PaymentFrequency.weekly;
            default:
                return PaymentFrequency.semiMonthly;
        }
    };

    const getPaymentType = () => {
        return paymentType === PaymentType.weeklyAdvance ? PaymentType.advance : paymentType;
    };

    const reactHookFormMethods = useForm({
        mode: 'all',
        defaultValues: {
            new_starting_payment_number: null,
            payment_type: getPaymentType(),
            payment_frequency: getPaymentFrequency(),
            payee_type: payeeType,
            payee_ids: '',
            start_date: startDate,
            end_date: endDate,
        },
        context: { requiresCheckNumber: numberOfChecks > 0 || forceCheckNumberInput },
        resolver: yupResolver(
            object({
                new_starting_payment_number: number()
                    .nullable()
                    .when('$requiresCheckNumber', (requiresCheckNumber, schema) => {
                        return requiresCheckNumber ? schema.required('Starting check number is required').min(1).typeError('Starting check number is required') : schema.notRequired();
                    }),
            })
        ),
    });
    const { handleSubmit, setValue, watch } = reactHookFormMethods;
    const watchStartingNumber = watch('new_starting_payment_number');

    const fetchPreview = useCallback(
        debounce(async (checkCount, startingCheckNumber, forceCheckNumber) => {
            if (checkCount === 0 && !forceCheckNumber) {
                setPreview(null);
                setPreviewError(null);
                return;
            }
            if (previewAbortRef.current) {
                previewAbortRef.current.abort = true;
            }
            const abortToken = { abort: false };
            previewAbortRef.current = abortToken;
            setPreviewLoading(true);
            setPreviewError(null);
            try {
                const previewInput = { paymentCategory: 'payroll', numberOfChecks: Math.max(checkCount, 1) };
                if (startingCheckNumber != null && startingCheckNumber !== '') {
                    previewInput.startingCheckNumber = Number(startingCheckNumber);
                }
                const result = await getNextAvailableCheckNumber(previewInput);
                if (!abortToken.abort) {
                    setPreview(result);
                    if (startingCheckNumber == null || startingCheckNumber === '') {
                        isAutoSettingRef.current = true;
                        setValue('new_starting_payment_number', result.suggestedStartingCheckNumber);
                    }
                }
            } catch (error) {
                if (!abortToken.abort) {
                    setPreviewError(error.message);
                    setPreview(null);
                }
            } finally {
                if (!abortToken.abort) {
                    setPreviewLoading(false);
                }
            }
        }, 300),
        [setValue]
    );

    const getPayeeIdsFromPayReports = () => {
        if (payeeType === PayReportPageType.hauler) {
            return payReports?.map((report) => report.hauler.id);
        }
        if (payeeType === PayReportPageType.vendor) {
            return payReports?.map((report) => report.vendor.id);
        }
        if (payeeType === PayReportPageType.producerBusiness) {
            return payReports?.map((report) => report._id);
        }
        if (paymentType === PaymentType.weeklyAdvance) {
            return payReports?.map((report) => report.producer_id);
        }
        return payReports?.map((report) => report.producer);
    };

    // Form setup — only on open / data changes
    useEffect(() => {
        if (dialogOpen) {
            setValue('payee_ids', getPayeeIdsFromPayReports());
        }
    }, [dialogOpen, payReports, payeeType, setValue]);

    useEffect(() => {
        if (!dialogOpen) return;
        if (isAutoSettingRef.current) {
            isAutoSettingRef.current = false;
            return;
        }
        if (watchStartingNumber != null && watchStartingNumber !== '') {
            fetchPreview(numberOfChecks, watchStartingNumber, forceCheckNumberInput);
        } else {
            fetchPreview(numberOfChecks, null, forceCheckNumberInput);
        }
    }, [dialogOpen, payReports, payeeType, watchStartingNumber, fetchPreview, numberOfChecks]);

    useEffect(() => {
        setValue('start_date', startDate);
    }, [startDate]);

    useEffect(() => {
        setValue('end_date', endDate);
    }, [endDate]);

    useEffect(() => {
        setValue('payment_type', getPaymentType());
        setValue('payment_frequency', getPaymentFrequency());
    }, [paymentType]);

    const handleClose = () => {
        setDialogOpen(false);
        setPreview(null);
        setPreviewError(null);
        setValue('new_starting_payment_number', null);
        setForceCheckNumberInput(false);
    };

    const confirmStartingCheckNumber = (model) => {
        if (numberOfChecks > 0 && !previewError && (previewLoading || !preview || preview.canGenerate === false)) {
            return;
        }
        const normalizedModel = {
            ...model,
            new_starting_payment_number: numberOfChecks === 0 && !forceCheckNumberInput ? null : model.new_starting_payment_number,
        };
        setIsSubmitting(true);
        dispatch(addPayReportPaymentStatus(normalizedModel))
            .then(() => {
                dispatchTask({ payReportDownloadStatus: PayReportDownloadStatus.download });
                handleClose();
            })
            .catch((error) => {
                dispatch(showMessage({ message: error.message }));
                setForceCheckNumberInput(true);
            })
            .finally(() => {
                setIsSubmitting(false);
            });
    };

    const debounceSubmit = useCallback(debounce(confirmStartingCheckNumber, 500), [dispatchTask, preview, previewLoading, numberOfChecks, forceCheckNumberInput]);

    const hasConflicts = preview?.conflictingCheckNumbers?.length > 0;
    const downloadDisabled = isSubmitting || previewLoading || (preview != null && (hasConflicts || preview.canGenerate === false));
    const collisionDisplay = hasConflicts ? formatCollisionList(preview.conflictingCheckNumbers) : '';

    return (
        <div>
            <Dialog open={dialogOpen} onClose={handleClose} PaperComponent={PaperComponent} fullWidth maxWidth="sm">
                <DialogTitle id="draggable-dialog-title">Confirm Starting Check Number</DialogTitle>
                <FormProvider {...reactHookFormMethods}>
                    <form onSubmit={handleSubmit(debounceSubmit)}>
                        <DialogContent>
                            {numberOfChecks === 0 && !forceCheckNumberInput && (
                                <Alert severity="info" style={{ marginBottom: 8 }} role="alert">
                                    No check payments in this run — you are good to proceed. Click CONTINUE to apply the ACH payment numbers. You will be able to download the payment file from the Payment Status page.
                                </Alert>
                            )}
                            {(numberOfChecks > 0 || forceCheckNumberInput) && <ReactHookFormInput label={'Download all pay reports from check number:'} name={'new_starting_payment_number'} type={'number'} />}
                            {previewLoading && (
                                <div style={{ display: 'flex', alignItems: 'center', marginTop: 8 }}>
                                    <CircularProgress size={20} style={{ marginRight: 8 }} />
                                    <span>Checking for conflicts...</span>
                                </div>
                            )}
                            {hasConflicts && (
                                <Alert severity="error" style={{ marginTop: 8 }} role="alert">
                                    <AlertTitle>Check numbers already in use</AlertTitle>
                                    Check numbers already in use: {collisionDisplay}. Please choose a different starting number.
                                </Alert>
                            )}
                            {previewError && (
                                <Alert severity="error" style={{ marginTop: 8 }} role="alert">
                                    {previewError}
                                </Alert>
                            )}
                        </DialogContent>
                        <DialogActions>
                            <Button type="submit" variant="contained" color="primary" disabled={downloadDisabled}>
                                {isSubmitting ? 'Loading' : 'Continue'}
                            </Button>
                            <Button onClick={handleClose} color="primary">
                                Cancel
                            </Button>
                        </DialogActions>
                    </form>
                </FormProvider>
            </Dialog>
        </div>
    );
};

export default ConfirmCheckNumberDialog;
